import {Asset} from '../../../types';
import AclForm from '../../Permissions/AclForm.tsx';
import {
    AclExtraPermission,
    AclPermission,
    aclPermissions,
    PermissionDefinitionOverride,
    PermissionObject,
    PermissionType,
} from '../../Permissions/permissionsTypes.ts';
import ParentAcl from '../../Permissions/ParentAcl.tsx';
import {Trans} from 'react-i18next';
import WorkspaceAclForm from '../Workspace/WorkspaceAclForm.tsx';
import React, {useMemo} from 'react';
import CollectionAclForm from '../Collection/CollectionAclForm.tsx';
import {AclFormProps} from '../../Permissions/aclTypes.ts';
import {useTranslation} from 'react-i18next';
import {getAclDescriptions} from '../Workspace/aclDescriptions.ts';

type Props = AclFormProps<Asset>;

export default function AssetAclForm({
    data,
    workspaceInheritance,
    helper,
    parentDisplay,
}: Props) {
    const {t} = useTranslation();
    const definitions: PermissionDefinitionOverride[] = useMemo(() => {
        const aclDescriptions = getAclDescriptions(t);

        return [
            {
                type: PermissionType.Mask,
                key: AclPermission.VIEW,
                label: t('acl.permission.asset.view.label', 'View'),
                description: t(
                    'acl.permission.asset.view.desc',
                    'Allows viewing this asset, but not necessarily the collection to which it belongs.'
                ),
            },
            {
                type: PermissionType.Mask,
                key: AclPermission.EDIT,
                label: t('acl.permission.asset.edit.label', 'Edit Attributes'),
                description: t(
                    'acl.permission.asset.edit.desc',
                    'Allows editing the asset’s attributes.'
                ),
            },
            {
                type: PermissionType.Mask,
                key: AclPermission.OPERATOR,
                label: t('acl.permission.asset.operator.label', 'Manage'),
                description: t('acl.permission.asset.operator.desc', {
                    defaultValue:
                        'Allows management actions on the asset ({{manage_asset_desc}}).',
                    manage_asset_desc: aclDescriptions.aclOperatorDesc,
                }),
            },

            {
                type: PermissionType.Extra,
                key: AclExtraPermission.EDIT_PERMISSIONS,
                value: AclExtraPermission.EDIT_PERMISSIONS,
                label: t(
                    'acl.permission.asset.edit_permissions.label',
                    'Edit Permissions/Privacy'
                ),
                description: t(
                    'acl.permission.asset.edit_permissions.desc',
                    'Allows changing permissions and privacy settings for assets you own.'
                ),
            },

            {
                type: PermissionType.Mask,
                key: AclPermission.DELETE,
                label: t('acl.permission.asset.delete.label', 'Delete'),
                description: t(
                    'acl.permission.asset.delete.desc',
                    'Allows deleting this asset.'
                ),
            },

            {
                type: PermissionType.Mask,
                key: AclPermission.OWNER,
                label: t('acl.permission.asset.owner.label', 'Owner'),
                description: t(
                    'acl.permission.asset.owner.desc',
                    'Full control over this asset, except for permissions and privacy settings.'
                ),
            },
        ];
    }, [t]);

    return (
        <>
            <AclForm
                helper={helper}
                objectId={data.id}
                objectType={PermissionObject.Asset}
                definitions={definitions}
                filterDefinitions={({value, key}) =>
                    value < aclPermissions[AclPermission.CHILD_CREATE] &&
                    ![
                        AclPermission.CREATE,
                        AclPermission.UNDELETE,
                        AclPermission.MASTER,
                    ].includes(key as AclPermission)
                }
            />
            {data.collections?.map(c =>
                c.storyAsset ? (
                    <ParentAcl
                        key={c.id}
                        title={
                            <Trans
                                i18nKey={'collection.acl.parent.story'}
                                defaults={`Permissions on Story <strong>{{name}}</strong>`}
                                values={{
                                    name: c.storyAsset.name,
                                }}
                            />
                        }
                        name={c.storyAsset.name || 'Story'}
                    >
                        <AssetAclForm
                            data={c.storyAsset!}
                            parentDisplay={true}
                        />
                    </ParentAcl>
                ) : (
                    <ParentAcl
                        key={c.id}
                        title={
                            <Trans
                                i18nKey={'collection.acl.parent.collection'}
                                defaults={`Permissions on <strong>{{name}}</strong>`}
                                values={{
                                    name: c.displayName,
                                }}
                            />
                        }
                        name={c.displayName}
                        parentDisplay={parentDisplay}
                    >
                        <CollectionAclForm data={c} parentDisplay={true} />
                    </ParentAcl>
                )
            )}

            {workspaceInheritance ? (
                <ParentAcl
                    name={data.workspace.displayName}
                    title={
                        <Trans
                            i18nKey={'collection.acl.parent.workspace'}
                            defaults={`Permissions on Workspace <strong>{{name}}</strong>`}
                            values={{
                                name: data.workspace.displayName,
                            }}
                        />
                    }
                    parentDisplay={parentDisplay}
                >
                    <WorkspaceAclForm
                        data={data.workspace}
                        parentDisplay={true}
                    />
                </ParentAcl>
            ) : null}
        </>
    );
}
